/**
 * โหลด genealogy หนึ่งทิศทางของ lot หนึ่งใบ
 *
 * แยกออกมาเป็น hook เพราะหน้าจอเรียกมัน **สองครั้งพร้อมกัน** ทิศทางละครั้ง และ
 * ทั้งสองต้องมีสถานะของตัวเองแยกขาดจากกัน: การที่ `forward` ล้มเหลวต้องไม่ลบ
 * `backward` ที่โหลดสำเร็จแล้วออกจากหน้าจอ และในทางกลับกันด้วย — บนงานเรียกคืน
 * สินค้า ครึ่งเดียวที่อ่านได้ยังมีค่ามากกว่าหน้าจอว่างที่บอกว่าทั้งหมดล้มเหลว
 *
 * สามสถานะที่ต้องแยกจากกันเสมอ: กำลังโหลด / สำเร็จแต่ไม่มีความสัมพันธ์ / โหลด
 * ไม่สำเร็จ รายการว่างที่ใช้แทนทั้งสองอย่างหลังคือหน้าจอที่โกหกผู้ใช้ — และที่นี่มัน
 * โกหกเรื่องที่แพงที่สุดในระบบ: "lot นี้ไม่ได้ไปไหนต่อ" กับ "อ่านไม่ได้ว่ามันไปไหน"
 * นำไปสู่การเรียกคืนสินค้าคนละขนาดกัน
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  fetchLotGenealogy,
  type LotGenealogyFailure,
} from '../../lib/lotTraceability/client';
import type {
  GenealogyDirection,
  LotGenealogyEdge,
  LotGenealogyLot,
} from '../../lib/lotTraceability/types';

export type LotGenealogyState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly lot: LotGenealogyLot;
      readonly edges: readonly LotGenealogyEdge[];
      readonly nextCursor: string | null;
      readonly loadingMore: boolean;
      /** ความล้มเหลวของ "โหลดเพิ่ม" ไม่ควรลบ edge ที่โหลดมาแล้วทิ้ง */
      readonly loadMoreFailure: LotGenealogyFailure | null;
    }
  | LotGenealogyFailure;

export interface UseLotGenealogyOptions {
  readonly lotId: string;
  readonly direction: GenealogyDirection;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

export interface UseLotGenealogyResult {
  readonly state: LotGenealogyState;
  /** ขอหน้าถัดไปของ `edges` — merge ที่มี parent มากกว่าหนึ่งหน้าต้องตามให้ครบ */
  readonly loadMore: () => void;
  readonly retry: () => void;
}

export function useLotGenealogy({
  lotId,
  direction,
  apiBaseUrl,
  fetchImpl,
}: UseLotGenealogyOptions): UseLotGenealogyResult {
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<LotGenealogyState>({ status: 'loading' });

  /**
   * รุ่นของคำขอที่กำลังแสดงอยู่ — เพิ่มทุกครั้งที่เปลี่ยน lot/ทิศทาง/โหลดใหม่
   *
   * จำเป็นเพราะ "โหลดหน้าถัดไป" ไม่ได้ถูกยกเลิกพร้อม `AbortController` ของ effect:
   * ผู้ใช้ที่กดโหลดเพิ่มแล้วคลิก edge ไปยัง lot อื่นทันที จะได้คำตอบของหน้าถัดไป
   * ของ **lot เดิม** กลับมาหลังหน้าจอเปลี่ยน lot ไปแล้ว การต่อท้ายมันเข้าไปคือการ
   * ปน genealogy ของสอง lot เข้าด้วยกัน ซึ่งเป็นข้อมูลที่ผิดแบบที่มองไม่ออกว่าผิด
   */
  const generation = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    const current = generation.current + 1;
    generation.current = current;

    setState({ status: 'loading' });

    void fetchLotGenealogy(
      lotId,
      direction,
      {},
      { apiBaseUrl, fetchImpl, signal: controller.signal },
    ).then((result) => {
      if (generation.current !== current || result.status === 'cancelled') {
        return;
      }
      setState(
        result.status === 'ready'
          ? {
              status: 'ready',
              lot: result.genealogy.lot,
              edges: result.genealogy.edges,
              nextCursor: result.genealogy.next_cursor,
              loadingMore: false,
              loadMoreFailure: null,
            }
          : result,
      );
    });

    return () => {
      controller.abort();
    };
  }, [lotId, direction, apiBaseUrl, fetchImpl, reloadToken]);

  const retry = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const loadMore = useCallback(async () => {
    if (state.status !== 'ready' || state.loadingMore) {
      return;
    }
    const loaded = state;
    const cursor = loaded.nextCursor;
    if (cursor === null) {
      return;
    }
    const current = generation.current;
    setState({ ...loaded, loadingMore: true, loadMoreFailure: null });

    const result = await fetchLotGenealogy(
      lotId,
      direction,
      { cursor },
      { apiBaseUrl, fetchImpl },
    );
    if (generation.current !== current || result.status === 'cancelled') {
      return;
    }
    setState(
      result.status === 'ready'
        ? {
            status: 'ready',
            lot: result.genealogy.lot,
            edges: [...loaded.edges, ...result.genealogy.edges],
            nextCursor: result.genealogy.next_cursor,
            loadingMore: false,
            loadMoreFailure: null,
          }
        : { ...loaded, loadingMore: false, loadMoreFailure: result },
    );
  }, [state, lotId, direction, apiBaseUrl, fetchImpl]);

  return {
    state,
    loadMore: () => {
      void loadMore();
    },
    retry,
  };
}
